import React from 'react'
import { arrayOf, bool, number, oneOfType, shape, string } from 'prop-types'
import { css } from '@emotion/core'
import { useContainerWidth, PersonBlock, H3 } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import getRelationshipStringKeyFromAscendancyNumber from '../../../../../shared/helpers/relationship_helper'
import { extractFourDigitYear } from '../../../../../shared/helpers/factDate_Helper'

export default function Event({ gender, thumbnailIcon, person, year, showPersonBlock, events }) {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()

  const mainStyle = css`
    display: flex;
    justify-content: center;
    flex-direction: ${atWidth({ default: 'column', md: 'row' })};
  `
  const eventListContainerStyle = css`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: flex-start;
  `
  const headlineStyle = (twoOrMoreEvents) => css`
    font-size: ${atWidth({ default: '16px', md: '20px', lg: '24px' })};
    ${twoOrMoreEvents && 'display: list-item;'};
    ${twoOrMoreEvents && 'list-style-type: circle;'};
  `

  const subheaderCss = css`
    font-weight: 700;
    font-size: 14px;
  `

  const birthYear = extractFourDigitYear(person?.dob)
  const age = year - birthYear
  const strType = age > 0 ? 'yearTurnedAge' : 'yearBorn'
  const maxW = showPersonBlock ? '400px' : '700px'

  // eslint-disable-next-line react/no-unstable-nested-components
  function TextAdditional({ story }) {
    const pureText = DOMPurify.sanitize(story.textAdditional)
    // eslint-disable-next-line react/no-danger
    return <span css={subheaderCss} dangerouslySetInnerHTML={{ __html: pureText }} />
  }

  return (
    <div css={mainStyle}>
      <div css={{ width: '100%', maxWidth: maxW }}>
        {showPersonBlock && (
          <div css={{ padding: atWidth({ default: '10px', lg: '20px' }) }}>
            <PersonBlock
              size={atWidth({ default: 'md', lg: 'lg', xl: 'xl' })}
              avatarProps={{
                sex: gender,
                src: thumbnailIcon,
              }}
              name={`${year}`}
              details={
                person?.atn && t(getRelationshipStringKeyFromAscendancyNumber(person?.atn, gender, strType), { age })
              }
            />
          </div>
        )}
        <div css={eventListContainerStyle}>
          {events?.length > 0 &&
            events.map((story) => (
              <div key={story.id} css={{ marginBottom: atWidth({ default: '10px', lg: '20px' }) }}>
                <div
                  css={headlineStyle(events?.length > 1)}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(story.textHeadline),
                  }}
                />
                {story?.textAdditional && <TextAdditional story={story} />}
              </div>
            ))}
        </div>
        {!events && <H3 centered>{t('aam.no.data.for.year', "We don't have data for the selected year")}</H3>}
      </div>
    </div>
  )
}

Event.propTypes = {
  gender: string,
  thumbnailIcon: string,
  person: shape({
    active: bool,
    atn: oneOfType([number, string]),
    displayName: string,
    dob: oneOfType([number, string]),
    dod: string,
    gender: string,
    genderType: string,
    lifespan: string,
    living: bool,
    name: string,
    personId: string,
    pob: string,
    pod: string,
    portraitImageLink: string,
    portraitThumbnailLink: string,
    surname: string,
  }),
  year: oneOfType([string, number]),
  showPersonBlock: bool,
  events: arrayOf(
    shape({
      complete: bool,
      error: bool,
      load: bool,
      pending: bool,
      res: arrayOf(
        shape({
          category: string,
          categoryId: number,
          endYear: number,
          icon: string,
          id: number,
          imageUrl: string,
          languageOnly: string,
          localeOnly: string,
          startYear: number,
          subcategory: string,
          textHeadline: string,
          textTitle: string,
          tileType: string,
          year: string,
        })
      ),
    })
  ),
}
